import React from 'react';
import { Lock, ArrowDownward, ArrowUpward } from '@mui/icons-material';

const formatAmount = (value, currency) => {
    const n = Number(value) || 0;
    const formatted = Math.abs(n).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const prefix = currency === 'CDF' ? 'CDF' : '$';
    if (n < 0) return `−${prefix} ${formatted}`;
    if (n > 0) return `${prefix} ${formatted}`;
    return `${prefix} 0,00`;
};

/**
 * incomingBalance / outgoingBalance viennent de GET /trs/accounts.
 * Ce ne sont pas des totaux de transactions du jour :
 * - incoming = encours « entrantes » approuvées mais pas encore libérées sur le solde principal
 * - outgoing = encours « sortantes » approuvées mais pas encore finalisées
 */
const BlockedBalanceCard = ({ account, currency, loading }) => {
    const crc = account?.currency || currency;
    const incoming = Number(account?.incomingBalance ?? 0);
    const outgoing = Number(account?.outgoingBalance ?? 0);

    const rows = [
        {
            key: 'incoming',
            label: 'Paiement (en attente)',
            hint: 'Paiements clients validés, pas encore crédités sur la balance principale',
            value: incoming,
            icon: ArrowDownward,
            iconClass: incoming < 0 ? 'text-amber-600' : 'text-emerald-600',
        },
        {
            key: 'outgoing',
            label: 'Retrait (en attente)',
            hint: 'Retraits ou sorties validés, pas encore débités définitivement',
            value: outgoing,
            icon: ArrowUpward,
            iconClass: outgoing < 0 ? 'text-amber-600' : 'text-orange-600',
        },
    ];

    return (
        <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border-2 border-amber-200/80 bg-gradient-to-br from-white via-amber-50/40 to-orange-50/30 shadow-xl shadow-amber-500/10">
            <div className="pointer-events-none absolute top-0 right-0 h-32 w-32 rounded-full bg-amber-400/10 blur-2xl" />
            <div className="relative z-10 flex flex-1 flex-col p-5 lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30">
                        <Lock className="text-2xl text-white" aria-hidden />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800/70">
                            En Attente
                        </p>
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Balance en attente</h2>
                    </div>
                </div>

                <div className="mt-auto grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {rows.map(({ key, label, hint, value, icon: Icon, iconClass }) => (
                        <div
                            key={key}
                            className={`rounded-xl border bg-white/80 px-4 py-3 transition-colors duration-200 ${
                                (key === 'incoming' ? incoming : outgoing) < 0
                                    ? 'border-amber-300/80 bg-amber-50/50'
                                    : 'border-amber-100'
                            }`}
                            title={hint}
                        >
                            <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                <Icon className={`text-sm ${iconClass}`} aria-hidden />
                                {label}
                            </p>
                            <p
                                className={`text-xl font-bold tabular-nums leading-tight lg:text-2xl ${
                                    value < 0 ? 'text-amber-800' : 'text-slate-900'
                                }`}
                            >
                                {loading ? (
                                    <span className="inline-block h-7 w-24 animate-pulse rounded bg-slate-200" />
                                ) : (
                                    formatAmount(value, crc)
                                )}
                            </p>
                            <p className="mt-1 text-[10px] leading-tight text-slate-500">{hint}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BlockedBalanceCard;
